import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import type {
  ChannelTabType,
  Communication,
  CommunicationChannelKey,
  HandledStatusFilter,
} from '~/components/communications/types';
import { isValidChannelTabType } from '~/components/communications/types';
import type { InnerTabSingleProps } from '~/components/core/Layout/InnerTabs/InnerTabs';
import type { PaginationState } from '~/hooks/useIdBasedPagination';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { COMMUNICATION_CHANNEL_DICT } from '~/Types';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import type { UserModel } from '../../types/user-types';
import type { CommunicationsTablePanelProps } from '../CommunicationsTablePanel';
import CommunicationsTablePanel from '../CommunicationsTablePanel';
import { markCommunicationAsRead } from '../utils/markCommunicationAsRead';

import { useCommunicationsPagination } from './useCommunicationsPagination';
import { useHandledStatusFilter } from './useHandledStatusFilter';

interface CommunicationAdjuster {
  id: string;
  username: string;
}

interface SortByColumn {
  id: string;
  order: 'asc' | 'desc';
}

interface UseCommunicationsTabConfig {
  // Context configuration
  claimId?: number;
  organizationId?: number;
  userId?: string;
  user: UserModel;

  // Pagination configuration
  communicationsPerPage: number;

  // Column configuration
  removeExposuresColumn: boolean;
  removeAdjusterColumn: boolean;
  addClaimColumn: boolean;

  // Filter configuration
  displayAdjusterFilter: boolean;
  displayExposuresFilter: boolean;
  displayHandledStatusFilter?: boolean;
  isHandledStatusEnabled?: boolean;
  handledFilterDefaultsToPending?: boolean;
  isCommunicationConfigLoading?: boolean;

  // Optional configurations
  initialFilteredExposuresIds?: number[];
  organizationContactRolesDict?: Record<string, unknown>;
  onClaimUpdate?: () => Promise<PopulatedClaim | void>;

  // Clive configuration
  showClive: boolean;

  // Additional props for CommunicationsTablePanel
  additionalTableProps?: Partial<CommunicationsTablePanelProps>;
}

interface UseCommunicationsTabReturn {
  // State
  activeChannelType: ChannelTabType;
  selectedContact: ContactFullModel | null;
  selectedAdjuster: CommunicationAdjuster | null;
  selectedCommunicationId: number | undefined;
  filteredExposuresIds: number[];
  searchText: string;
  displaySearchText: string;
  sortByColumn: SortByColumn;

  // Data
  communications: Communication[];
  pageMeta: Record<string, unknown> | null;
  isLoading: boolean;
  isError: boolean;
  pagination: PaginationState | null;
  unreadRowsIds: number[];
  communicationsReadStatusDict: Record<string, boolean>;

  // Handlers
  handleTabClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    tab: InnerTabSingleProps<CommunicationsTablePanelProps>
  ) => void;
  handleSelectCommunication: (communicationId: number | null) => Promise<void>;
  handleSearchTextChange: (searchValue: string) => void;
  handleClearText: () => void;
  handleClearFilters: () => void;
  handleChangeSelectedChannelsFilter: (channels: CommunicationChannelKey[] | null) => void;
  handleRefresh: () => void;

  // Computed
  tabs: InnerTabSingleProps<CommunicationsTablePanelProps>[];
  displayLoading: boolean;
}

export const useCommunicationsTab = (config: UseCommunicationsTabConfig): UseCommunicationsTabReturn => {
  const {
    claimId,
    organizationId,
    userId,
    user,
    communicationsPerPage,
    removeExposuresColumn,
    removeAdjusterColumn,
    addClaimColumn,
    displayAdjusterFilter,
    displayExposuresFilter,
    displayHandledStatusFilter = false,
    isHandledStatusEnabled = false,
    handledFilterDefaultsToPending = false,
    isCommunicationConfigLoading = false,
    initialFilteredExposuresIds = [],
    organizationContactRolesDict,
    onClaimUpdate,
    showClive,
    additionalTableProps = {},
  } = config;

  // State
  const [selectedContact, setSelectedContact] = useState<ContactFullModel | null>(null);
  const [selectedAdjuster, setSelectedAdjuster] = useState<CommunicationAdjuster | null>(null);
  const [selectedCommunicationId, setSelectedCommunicationId] = useState<number | undefined>(undefined);
  const [filteredExposuresIds, setFilteredExposuresIds] = useState<number[]>(initialFilteredExposuresIds);
  const [activeChannelType, setActiveChannelType] = useState<ChannelTabType>('all');
  const [selectedChannelsFilter, setSelectedChannelsFilter] = useState<CommunicationChannelKey[] | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [displaySearchText, setDisplaySearchText] = useState<string>('');
  const [initialAvailableChannels, setInitialAvailableChannels] = useState<ChannelTabType[]>([]);
  const [sortByColumn, setSortByColumn] = useState<SortByColumn>({ id: 'datetime', order: 'desc' });

  const isHandledStatusFilterAvailable = displayHandledStatusFilter && isHandledStatusEnabled;
  const {
    handledStatusFilter,
    setHandledStatusFilter,
    resetHandledStatusFilter,
    isDefaultState: isHandledStatusDefaultState,
    handledStatusParam,
  } = useHandledStatusFilter({
    isEnabled: isHandledStatusFilterAvailable,
    defaultsToPending: handledFilterDefaultsToPending,
    isConfigLoading: isCommunicationConfigLoading,
  });

  const formatSortByColumn = (sortObj: SortByColumn): string => {
    if (!sortObj || !sortObj.id) return '-datetime';
    const prefix = sortObj.order === 'asc' ? '+' : '-';
    return `${prefix}${sortObj.id}`;
  };

  const communicationChannelParam: CommunicationChannelKey | null =
    activeChannelType === 'all' ? null : activeChannelType;
  const communicationChannelsParam: CommunicationChannelKey[] | null =
    activeChannelType === 'all' && selectedChannelsFilter && selectedChannelsFilter?.length > 0
      ? selectedChannelsFilter
      : null;

  const paginationParams = {
    claimId,
    organizationId,
    userId: displayAdjusterFilter ? (selectedAdjuster?.id ?? null) : userId,
    initialRowsPerPage: communicationsPerPage,
    sortByColumn: claimId ? formatSortByColumn(sortByColumn) : undefined,
    exposuresFilters: filteredExposuresIds?.length > 0 ? filteredExposuresIds : null,
    contactId: selectedContact?.id ?? null,
    communicationChannel: communicationChannelParam,
    communicationChannels: communicationChannelsParam,
    categoriesIds: selectedCategories?.length > 0 ? selectedCategories : null,
    searchText,
    handledStatus: handledStatusParam ?? null,
  };

  const {
    pagination,
    pageResults: communications,
    pageMeta,
    extraData,
    isLoading,
    isError,
    reload: reloadData,
    reloadKeepingPage,
    refreshPageData,
  } = useCommunicationsPagination(paginationParams);

  const { setPage } = pagination;
  const communicationAdjusters: CommunicationAdjuster[] = pageMeta?.communications_adjusters || [];
  const communicationsReadStatusDict: Record<string, boolean> = pageMeta?.communications_read_status || {};
  const displayLoading = isLoading && (!communications || communications.length === 0);

  // Capture initial available channels once when first loaded to keep tabs stable during filtering.
  // Both ids responses carry them, so the tabs survive a filter that matches nothing — an empty ids
  // list skips the page-data request altogether and leaves its meta null. The meta remains the
  // fallback for a response predating that extra data.
  const availableChannelsFromResponse: ChannelTabType[] | undefined =
    extraData?.available_channels ?? pageMeta?.available_channels;
  useEffect(() => {
    if (availableChannelsFromResponse?.length && initialAvailableChannels.length === 0) {
      setInitialAvailableChannels(availableChannelsFromResponse);
    }
  }, [availableChannelsFromResponse, initialAvailableChannels.length]);

  const unreadRowsIds: number[] =
    communications
      ?.filter((communication: Communication) => !communicationsReadStatusDict?.[communication.id])
      .map((n: Communication) => n.id) || [];

  const handleSelectCommunication = async (communicationId: number | null): Promise<void> => {
    setSelectedCommunicationId(communicationId || undefined);

    if (!communicationId) return;

    await markCommunicationAsRead({
      communicationId,
      user,
      communicationsReadStatusDict,
      onRefresh: async () => {
        await refreshPageData();
      },
      throwOnError: true,
    });
  };

  // Create debounced function once and reuse it
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        if (searchValue.length >= 3 || searchValue.length === 0) {
          setSearchText(searchValue);
          setSelectedCommunicationId(undefined);
          setPage(0);
        }
      }, 500),
    [setSearchText, setPage, setSelectedCommunicationId]
  );

  // Cancel pending debounce on unmount to prevent stale state updates
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Flush any pending debounced search so the current search text is included
  // in the API call when another filter changes. Without this, changing a filter
  // (e.g. adjuster) while search text is typed but debounce hasn't fired yet
  // would make an API call without the search text.
  const flushPendingSearch = useCallback(() => {
    debouncedSearch.flush();
  }, [debouncedSearch]);

  const handleTabClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    tab: InnerTabSingleProps<CommunicationsTablePanelProps>
  ) => {
    flushPendingSearch();
    if (isValidChannelTabType(tab.url)) {
      setActiveChannelType(tab.url);
    }
    setSelectedCommunicationId(undefined);
    setPage(0);
  };

  const handleChangeSelectedChannelsFilter = (channels: CommunicationChannelKey[] | null): void => {
    flushPendingSearch();
    setSelectedChannelsFilter(channels);
    setSelectedCommunicationId(undefined);
    setPage(0);
  };

  const handleSearchTextChange = (searchValue: string): void => {
    setDisplaySearchText(searchValue);
    debouncedSearch(searchValue);
  };

  const handleClearText = (): void => {
    debouncedSearch.cancel();
    setSearchText('');
    setDisplaySearchText('');
    setSelectedCommunicationId(undefined);
    setPage(0);
  };

  const handleClearFilters = (): void => {
    debouncedSearch.cancel();
    setSelectedContact(null);
    if (displayAdjusterFilter) {
      setSelectedAdjuster(null);
    }
    setSelectedChannelsFilter(null);
    setSelectedCategories([]);
    resetHandledStatusFilter();
    handleClearText();
  };

  const commonTableProps: Omit<CommunicationsTablePanelProps, 'channelType'> = {
    displayLoading,
    isLoading,
    isError,
    communications: communications || [],
    selectedCommunicationId,
    onSelectCommunication: handleSelectCommunication,
    pagination,
    count: pagination.totalCount || 0,
    unreadRowsIds,
    onRefresh: reloadData,
    onRefreshKeepingPage: reloadKeepingPage,
    onRefreshCurrentPage: refreshPageData,
    onUpdate: onClaimUpdate || reloadKeepingPage,
    commsPerPage: communicationsPerPage,
    removeExposuresColumn,
    removeAdjusterColumn,
    addClaimColumn,
    showClive,
    // Filters props
    showFilters: true,
    selectedContact,
    onSelectContact: (contact: ContactFullModel | null) => {
      flushPendingSearch();
      setSelectedContact(contact || null);
      setSelectedCommunicationId(undefined);
      setPage(0);
    },
    selectedAdjuster,
    onSelectAdjuster: (adjuster: CommunicationAdjuster | null) => {
      flushPendingSearch();
      setSelectedAdjuster(adjuster || null);
      setSelectedCommunicationId(undefined);
      setPage(0);
    },
    communicationAdjusters,
    filteredExposuresIds,
    onUpdateFilteredExposures: (filteredExposuresIds: number[]) => {
      flushPendingSearch();
      setFilteredExposuresIds(filteredExposuresIds);
      setSelectedCommunicationId(undefined);
      setPage(0);
    },
    onClearFilters: handleClearFilters,
    organizationContactRolesDict,
    availableChannels: initialAvailableChannels,
    selectedChannelsFilter,
    onChangeSelectedChannelsFilter: handleChangeSelectedChannelsFilter,
    displayAdjusterFilter,
    displayExposuresFilter,
    searchText: displaySearchText,
    onSearchTextChange: handleSearchTextChange,
    onClearText: handleClearText,
    selectedCategories,
    onChangeSelectedCategories: (categoryIds: number[]) => {
      flushPendingSearch();
      setSelectedCategories(categoryIds);
      setSelectedCommunicationId(undefined);
      setPage(0);
    },
    isHandledStatusFilterAvailable,
    handledStatusFilter,
    isHandledStatusDefaultState,
    onChangeHandledStatusFilter: (value: HandledStatusFilter) => {
      flushPendingSearch();
      setHandledStatusFilter(value);
      setSelectedCommunicationId(undefined);
      setPage(0);
    },
    ...additionalTableProps,
  };

  const handleSortByColumn = claimId
    ? (_: unknown, newSortByColumn: SortByColumn) => {
        flushPendingSearch();
        setSortByColumn(newSortByColumn);
        setPage(0);
      }
    : undefined;

  if (claimId && handleSortByColumn) {
    commonTableProps.onSortByColumn = handleSortByColumn;
    commonTableProps.sortByColumn = sortByColumn;
  }

  const tabs: InnerTabSingleProps<CommunicationsTablePanelProps>[] = [
    {
      label: 'All',
      url: 'all',
      component: CommunicationsTablePanel,
      props: {
        ...commonTableProps,
        channelType: 'all',
      },
    },
    ...initialAvailableChannels
      .filter((channel) => channel !== 'all')
      .map((channel) => ({
        label: COMMUNICATION_CHANNEL_DICT[channel],
        url: channel,
        component: CommunicationsTablePanel,
        props: {
          ...commonTableProps,
          channelType: channel,
          addSubjectColumn: channel === 'email',
        },
      })),
  ];

  return {
    // State
    activeChannelType,
    selectedContact,
    selectedAdjuster,
    selectedCommunicationId,
    filteredExposuresIds,
    searchText,
    displaySearchText,
    sortByColumn,

    // Data
    communications,
    pageMeta,
    isLoading,
    isError,
    pagination,
    unreadRowsIds,
    communicationsReadStatusDict,

    // Handlers
    handleTabClick,
    handleSelectCommunication,
    handleSearchTextChange,
    handleClearText,
    handleClearFilters,
    handleChangeSelectedChannelsFilter,
    handleRefresh: reloadData,

    // Computed
    tabs,
    displayLoading,
  };
};

export type { UseCommunicationsTabConfig, UseCommunicationsTabReturn };
